import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const calculateProfileCompletion = (profile: any): number => {
    if (!profile) return 0;
    
    let score = 0;
    const isNewForm = profile.targetAudience !== undefined || profile.educationHistory !== undefined;

    if (isNewForm) {
        // Logic tính điểm cho form mới (AspirationFormState)
        const { name, phone, email, permanentAddress, addressBD, dateOfBirth, targetAudience, height, weight, educationHistory, workHistory, japaneseLevel, englishScore, desiredIndustry } = profile;

        // Thông tin cơ bản (20%)
        if (name && name.trim() !== '') score += 10;
        if (phone || email) score += 10;

        // Lý lịch cá nhân (20%)
        if (permanentAddress || addressBD) score += 10;
        if (dateOfBirth && targetAudience) score += 10;

        // Tình trạng Hình thể (20%)
        if (height) score += 10;
        if (weight) score += 10;

        // Học vấn & Kinh nghiệm (20%)
        if (educationHistory && Array.isArray(educationHistory) && educationHistory.length > 0) score += 10;
        if (workHistory && Array.isArray(workHistory) && workHistory.length > 0) score += 10;

        // Kỹ năng & Định hướng (20%)
        if ((japaneseLevel && japaneseLevel.jlpt) || englishScore) score += 10;
        if (desiredIndustry && Array.isArray(desiredIndustry) && desiredIndustry.length > 0) score += 10;
    } else {
        // Logic tính điểm cho form cũ (CandidateProfile logic AI)
        const { name, headline, location, about, education, experience, skills, personalInfo } = profile;

        // Nhóm 1: Thông tin cơ bản (20%)
        if (name && name.trim() !== '') score += 10;
        if (personalInfo && (personalInfo.phone || personalInfo.zalo || personalInfo.line || personalInfo.messenger)) {
            score += 10;
        }

        // Nhóm 2: Hồ sơ giới thiệu (20%)
        if (about && about.trim() !== '') score += 10;
        if (headline && headline.trim() !== '') score += 5;
        if (location && location.trim() !== '') score += 5;

        // Nhóm 3: Thông tin cá nhân & Thể chất (20%)
        if (personalInfo) {
            if (personalInfo.gender) score += 5;
            if (personalInfo.dateOfBirth) score += 5;
            if (personalInfo.height && personalInfo.weight) score += 5;
            if (personalInfo.hepatitisBStatus && personalInfo.tattooStatus) score += 5;
        }

        // Nhóm 4: Học vấn & Kinh nghiệm (20%)
        if (education && Array.isArray(education) && education.length > 0) score += 10;
        if (experience && Array.isArray(experience) && experience.length > 0) score += 10;

        // Nhóm 5: Kỹ năng & Ngoại ngữ (20%)
        if (personalInfo && (personalInfo.japaneseProficiency || personalInfo.englishProficiency)) score += 10;
        if (skills && Array.isArray(skills) && skills.length > 0) score += 10;
    }

    return Math.min(100, score);
};

export const validateProfileForApplication = (profile: any): boolean => {
    return calculateProfileCompletion(profile) >= 50;
};

export const formatPhoneNumberInput = (value: string, country: string): string => {
    if (!value) return '';
    const cleanValue = value.replace(/\D/g, '');

    if (country === '+84') { // Vietnam (10 digits total starting with 0)
        if (cleanValue.length === 0) return '';
        if (cleanValue.length <= 4) return `(${cleanValue}`;
        if (cleanValue.length <= 7) return `(${cleanValue.slice(0, 4)}) ${cleanValue.slice(4)}`;
        return `(${cleanValue.slice(0, 4)}) ${cleanValue.slice(4, 7)} ${cleanValue.slice(7, 10)}`;
    }

    if (country === '+81') { // Japan (11 digits total starting with 0)
        if (cleanValue.length === 0) return '';
        if (cleanValue.length <= 3) return `(${cleanValue}`;
        if (cleanValue.length <= 7) return `(${cleanValue.slice(0, 3)}) ${cleanValue.slice(3)}`;
        return `(${cleanValue.slice(0, 3)}) ${cleanValue.slice(3, 7)} ${cleanValue.slice(7, 11)}`;
    }

    return cleanValue;
};

export const formatAddress = (address: any): string => {
    if (!address || typeof address !== 'string') return '';
    
    // Xử lý các trường hợp null/undefined dạng chuỗi do API trả về đôi khi
    if (address === 'null' || address === 'undefined') return '';
    
    const capitalized = address.charAt(0).toUpperCase() + address.slice(1);
    
    const bdDivisionsSlugs = ["dhaka", "chittagong", "khulna", "rajshahi", "barisal", "sylhet", "rangpur", "mymensingh"];
    
    const bdDivisionsMap: Record<string, string> = {
        "dhaka": "Dhaka",
        "chittagong": "Chittagong",
        "khulna": "Khulna",
        "rajshahi": "Rajshahi",
        "barisal": "Barisal",
        "sylhet": "Sylhet",
        "rangpur": "Rangpur",
        "mymensingh": "Mymensingh"
    };

    const cleanAddress = address.trim().toLowerCase();
    
    if (bdDivisionsSlugs.includes(cleanAddress)) {
        return `${bdDivisionsMap[cleanAddress]} - Bangladesh`;
    }
    
    return capitalized;
};
